import { Row } from '@tanstack/react-table';
import { endOfDay, isAfter, isBefore, isEqual, startOfDay } from 'date-fns';
import { PosManagedMarketplace } from 'src/utils/constants/constants';
import {
  getIsInternationalEvent,
  getPerformerAndVenueForEvent,
} from 'src/utils/eventWithDataUtils';
import { safeParseJson } from 'src/utils/inventoryUtils';
import { getCurrentLanguage } from 'src/utils/localeUtils';
import {
  AutoPricingConstraintType,
  AutoPricingResultType,
  FilterOperationType,
  Marketplace,
} from 'src/WebApiController';

type Seating = {
  section: string;
  row?: string;
  seatFr?: string;
  seatTo?: string;
};

type ListingNote = {
  listingNoteId: number;
  listingNoteText: string;
};

type UiMoney = {
  amt: number;
  currency: string | null;
  disp: string | null;
  dec: number;
};

/**
 * Get currency symbol from currency code using Intl.NumberFormat
 */
export function getCurrencySymbol(currencyCode: string): string {
  try {
    const formatted = (0).toLocaleString(getCurrentLanguage(), {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    // Extract just the symbol (e.g., "$0" → "$", "€0" → "€")
    return formatted.replace(/[\d,.\s]/g, '').trim();
  } catch {
    return currencyCode; // Fallback to code if invalid currency
  }
}

/**
 * Number range filter - supports both range filtering (min-max) and discrete value selection
 */
export const numberRangeFilter = (
  row: Row<any>,
  columnId: string,
  filterValue:
    | [number | undefined, number | undefined]
    | { min?: number; max?: number; selectedValues?: string[] }
): boolean => {
  // Handle both old array format and new object format
  let min: number | undefined;
  let max: number | undefined;
  let selectedValues: string[] | undefined;

  if (Array.isArray(filterValue)) {
    [min, max] = filterValue;
  } else {
    min = filterValue.min;
    max = filterValue.max;
    selectedValues = filterValue.selectedValues;
  }

  // If nothing specified, show all
  if (
    min == null &&
    max == null &&
    (!selectedValues || selectedValues.length === 0)
  ) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => numberRangeFilter(leafRow, columnId, filterValue));
  }

  const value = row.getValue<number>(columnId);
  if (value == null) {
    return false;
  }

  // Check discrete values first (if any selected)
  if (selectedValues && selectedValues.length > 0) {
    return selectedValues.includes(String(value));
  }

  // Otherwise check range
  if (min != null && value < min) return false;
  if (max != null && value > max) return false;
  return true;
};

/**
 * Percent range filter - filters percentage values stored as decimals
 * Users enter percentages (15), but values are stored as decimals (0.15)
 */
export const percentRangeFilter = (
  row: Row<any>,
  columnId: string,
  filterValue:
    | [number | undefined, number | undefined]
    | { min?: number; max?: number; selectedValues?: string[] }
): boolean => {
  // Handle both old array format and new object format
  let min: number | undefined;
  let max: number | undefined;
  let selectedValues: string[] | undefined;

  if (Array.isArray(filterValue)) {
    [min, max] = filterValue;
  } else {
    min = filterValue.min;
    max = filterValue.max;
    selectedValues = filterValue.selectedValues;
  }

  if (
    min == null &&
    max == null &&
    (!selectedValues || selectedValues.length === 0)
  ) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => percentRangeFilter(leafRow, columnId, filterValue));
  }

  const value = row.getValue<number>(columnId);
  if (value == null) {
    return false;
  }

  // Check discrete values first (if any selected)
  // Note: selectedValues are strings like "0.15" (decimal format)
  if (selectedValues && selectedValues.length > 0) {
    return selectedValues.includes(String(value));
  }

  // Convert filter values from percentage to decimal
  // User enters 15 (meaning 15%), we compare against 0.15 (stored value)
  const minDecimal = min != null ? min / 100 : null;
  const maxDecimal = max != null ? max / 100 : null;

  if (minDecimal != null && value < minDecimal) return false;
  if (maxDecimal != null && value > maxDecimal) return false;
  return true;
};

/**
 * Multi-select filter - checks if value matches any selected option
 */
export const multiSelectFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: string[]
): boolean => {
  if (!filterValue || filterValue.length === 0) {
    return true;
  }

  // Pre-compute lowercase values once for better performance
  const filterValues = filterValue.map((value) => value.toLowerCase());

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => multiSelectFilter(leafRow, columnId, filterValue));
  }

  const value = row.getValue(columnId);
  if (value == null) {
    return false;
  }

  // Use includes for better performance
  return filterValues.includes(String(value).toLowerCase());
};

/**
 * Link text search filter - searches the text property of link column objects
 * Link columns store { text, link } objects, so we need to search the text property
 */
export const linkTextSearchFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: string
): boolean => {
  if (!filterValue) {
    return true;
  }
  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => linkTextSearchFilter(leafRow, columnId, filterValue));
  }

  const value = row.getValue(columnId) as {
    text: string | null;
    link: string | null;
  } | null;
  if (!value?.text) {
    return false;
  }

  return String(value.text).toLowerCase().includes(filterValue.toLowerCase());
};

export const textSearchFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: string
): boolean => {
  if (!filterValue) {
    return true;
  }
  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => textSearchFilter(leafRow, columnId, filterValue));
  }

  const value = row.getValue(columnId);

  if (!value) {
    return false;
  }

  return String(value).toLowerCase().includes(filterValue.toLowerCase());
};

/**
 * Seating section filter - checks if seating.section matches any selected values
 * Used with uniqueValueVariant: 'seatingSection'
 */
export const seatingSectionFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: string[]
): boolean => {
  if (!filterValue || filterValue.length === 0) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => seatingSectionFilter(leafRow, columnId, filterValue));
  }

  const value = row.getValue(columnId);
  if (!value) {
    return false;
  }

  const { seating } = value as { seating: Seating };
  return filterValue.includes(seating?.section ?? '');
};

/**
 * Seat traits filter - checks if any listing note IDs match the filter
 * Used with uniqueValueVariant: 'seatTraits'
 */
export const seatTraitsFilter = (
  row: Row<unknown>,
  columnId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterValue: any
): boolean => {
  if (!filterValue) {
    return true;
  }

  const filterListingNoteIds = (filterValue as string[]).map((value) =>
    Number(value)
  );
  if (filterListingNoteIds.length === 0) {
    return true;
  }

  // Skip if row has children
  if (row.getLeafRows().length > 0) {
    const leafRows = row.getLeafRows();
    for (const leafRow of leafRows) {
      if (seatTraitsFilter(leafRow, columnId, filterValue)) {
        return true;
      }
    }

    return false;
  }

  const value = row.getValue(columnId) as ListingNote[] | null | undefined;

  return (
    value?.some((note) => filterListingNoteIds.includes(note.listingNoteId)) ??
    false
  );
};

export const seatRangeFilter = (
  row: Row<unknown>,
  columnId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterValue: any
): boolean => {
  if (!filterValue) {
    return true;
  }

  // Handle both old array format and new object format
  let filterMin: number | undefined;
  let filterMax: number | undefined;
  let selectedValues: string[] | undefined;

  if (Array.isArray(filterValue)) {
    [filterMin, filterMax] = filterValue;
  } else {
    filterMin = filterValue.min;
    filterMax = filterValue.max;
    selectedValues = filterValue.selectedValues;
  }

  if (
    filterMin == null &&
    filterMax == null &&
    (!selectedValues || selectedValues.length === 0)
  ) {
    return true;
  }

  const seating = row.getValue<Seating>(columnId);

  if (!seating) {
    return false;
  }

  const seatFrNum = Number(seating.seatFr || 'NaN');
  const seatToNum = Number(seating.seatTo || 'NaN');

  // Check discrete values first (if any selected)
  if (selectedValues && selectedValues.length > 0) {
    // Check if any of the selected seat numbers match seatFr or seatTo
    return selectedValues.some((selectedSeat) => {
      const selectedNum = Number(selectedSeat);
      if (isNaN(selectedNum)) return false;

      if (!isNaN(seatFrNum) && !isNaN(seatToNum)) {
        // Check if selected seat is within the range [seatFr, seatTo]
        return selectedNum >= seatFrNum && selectedNum <= seatToNum;
      } else if (!isNaN(seatFrNum)) {
        return selectedNum === seatFrNum;
      }
      return false;
    });
  }

  // Otherwise check range
  let result = false;

  if (isNaN(seatFrNum) && isNaN(seatToNum)) {
    result = false;
  } else if (isNaN(seatToNum)) {
    if (filterMin == null) {
      result = filterMax != null && seatFrNum <= filterMax;
    } else if (filterMax == null) {
      result = seatFrNum >= filterMin;
    } else {
      result = seatFrNum >= filterMin && seatFrNum <= filterMax;
    }
  } else {
    // seatFrNum and seatToNum are both numbers
    // return true if [seatFrNum, seatToNum] and [filterMin, filterMax] have any overlap
    if (filterMin == null) {
      result = filterMax != null && seatFrNum <= filterMax;
    } else if (filterMax == null) {
      result = seatToNum >= filterMin;
    } else {
      result = seatFrNum <= filterMax && seatToNum >= filterMin;
    }
  }

  if (!result) {
    const leafRows = row.getLeafRows();
    for (const leafRow of leafRows) {
      if (seatRangeFilter(leafRow, columnId, filterValue)) {
        result = true;
        break;
      }
    }
  }

  return result;
};

/**
 * Date range filter - checks if date matches the filter criteria
 * Supports Date objects and date strings
 * Supports operations: Between, GreaterThan, GreaterThanOrEqual, LessThan, LessThanOrEqual
 */
export const dateRangeFilter = (
  row: Row<any>,
  columnId: string,
  filterValue:
    | [Date | undefined, Date | undefined]
    | {
        startDate?: Date;
        endDate?: Date;
        operation?: FilterOperationType;
      }
): boolean => {
  // Handle both old array format and new object format
  let startDate: Date | undefined;
  let endDate: Date | undefined;
  let operation: FilterOperationType = FilterOperationType.Between;

  if (Array.isArray(filterValue)) {
    [startDate, endDate] = filterValue;
  } else {
    startDate = filterValue.startDate;
    endDate = filterValue.endDate;
    operation = filterValue.operation ?? FilterOperationType.Between;
  }

  if (!startDate && !endDate) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => dateRangeFilter(leafRow, columnId, filterValue));
  }

  const value = row.getValue(columnId);
  if (value == null) {
    return false;
  }

  // Convert to Date object if it's a string
  const dateValue =
    value instanceof Date ? value : new Date(value as string | number);

  if (!dateValue || isNaN(dateValue.getTime())) {
    return false;
  }

  // Compare dates (ignoring time component)
  const dateToCheck = startOfDay(dateValue);

  // Handle different operations
  switch (operation) {
    case FilterOperationType.Between:
      // Between: check if date is within [startDate, endDate]
      if (startDate) {
        const min = startOfDay(startDate);
        if (isBefore(dateToCheck, min) && !isEqual(dateToCheck, min)) {
          return false;
        }
      }
      if (endDate) {
        const max = endOfDay(endDate);
        if (isAfter(dateToCheck, max) && !isEqual(dateToCheck, max)) {
          return false;
        }
      }
      return true;

    case FilterOperationType.GreaterThan:
      // After (exclusive)
      if (!startDate) return true;
      const gtDate = endOfDay(startDate);
      return isAfter(dateToCheck, gtDate);

    case FilterOperationType.GreaterThanOrEqual:
      // On or After (inclusive)
      if (!startDate) return true;
      const gteDate = startOfDay(startDate);
      return isAfter(dateToCheck, gteDate) || isEqual(dateToCheck, gteDate);

    case FilterOperationType.LessThan:
      // Before (exclusive)
      if (!endDate) return true;
      const ltDate = startOfDay(endDate);
      return isBefore(dateToCheck, ltDate);

    case FilterOperationType.LessThanOrEqual:
      // On or Before (inclusive)
      if (!endDate) return true;
      const lteDate = endOfDay(endDate);
      return isBefore(dateToCheck, lteDate) || isEqual(dateToCheck, lteDate);

    default:
      return true;
  }
};

/**
 * Outlier spacing filter - filters by absolute or relative spacing
 * Supports toggling between currency ($) and percentage (%) modes
 */
export const outlierSpacingFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: { mode?: 'absolute' | 'relative'; min?: number; max?: number }
): boolean => {
  const { mode, min, max } = filterValue;

  if (!mode || (min == null && max == null)) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => outlierSpacingFilter(leafRow, columnId, filterValue));
  }

  const listing = (row.original as any)?.listing;
  if (!listing) return false;

  const value =
    mode === 'absolute'
      ? listing.outKthLowestLimAbsSpc
      : listing.outKthLowestLimRelSpc;

  if (value == null) return false;

  if (min != null && value < min) return false;
  if (max != null && value > max) return false;

  return true;
};

/**
 * Comp listing minimum quantity filter - filters by quantity filter value from JSON
 * Parses compQtyScrAdjJson to extract quantityFilter number
 */
export const compListingMinQuantityFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: [number | undefined, number | undefined]
): boolean => {
  const [min, max] = filterValue;
  if (min == null && max == null) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) =>
        compListingMinQuantityFilter(leafRow, columnId, filterValue)
      );
  }

  // Parse JSON to get quantityFilter value
  const listing = (row.original as any)?.listing;
  const jsonString = listing?.compQtyScrAdjJson;
  if (!jsonString) return false;

  const parsed = safeParseJson(jsonString);
  const quantityFilter = parsed?.quantityFilter;

  if (quantityFilter == null) return false;

  if (min != null && quantityFilter < min) return false;
  if (max != null && quantityFilter > max) return false;
  return true;
};

/**
 * Boolean filter - filters boolean values
 * filterValue: null (show all), true (show only true), false (show only false)
 */
export const booleanFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: boolean | null
): boolean => {
  // If no filter (null), show all
  if (filterValue == null) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => booleanFilter(leafRow, columnId, filterValue));
  }

  const value = row.getValue(columnId);

  // Compare boolean values
  return value === filterValue;
};

/**
 * Outlier mode filter - filters by outlier mode (StandardDeviations/SkipLogic/KthLowest)
 * Uses standard multiSelect logic on the accessor value
 */
export const outlierModeFilter = multiSelectFilter;

/**
 * Outlier standard deviations filter - filters by standard deviation number
 * Reads listing.outlStdDev from row.original instead of accessor value
 */
export const outlierStdDevFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: string[]
): boolean => {
  if (!filterValue || filterValue.length === 0) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => outlierStdDevFilter(leafRow, columnId, filterValue));
  }

  // Read outlStdDev directly from row.original.listing
  const listing = (row.original as any)?.listing;
  if (listing?.outlStdDev == null) {
    return false;
  }

  // Check if the standard deviation number matches any selected value
  return filterValue.includes(String(listing.outlStdDev));
};

/**
 * Marketplace broadcast filter - filters by marketplaces where listing is Listed
 * Reads mkpListings from row.original instead of accessor value
 */
export const marketplaceBroadcastFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: string[]
): boolean => {
  if (!filterValue || filterValue.length === 0) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) =>
        marketplaceBroadcastFilter(leafRow, columnId, filterValue)
      );
  }

  // Read mkpListings directly from row.original (bypass accessor)
  const listing = (row.original as any)?.listing;
  if (!listing?.mkpListings) {
    return false;
  }

  // Get venue using catalog data (same as cell rendering)
  const event = (row.original as any)?.event;
  const table = (row as any).getTable?.();
  const catalogData = table?.options?.meta?.catalogData;
  let venue = null;
  if (event && catalogData) {
    const fullInfo = getPerformerAndVenueForEvent(event, catalogData);
    venue = fullInfo.venue;
  }
  const isInternationalEvent = getIsInternationalEvent(venue?.ctryCode);

  // Get marketplaces where status is Listed (matching cell rendering logic)
  const listedMarketplaces = listing.mkpListings
    .filter(
      (m: any) =>
        m.status === 'Listed' &&
        m.mkp &&
        !PosManagedMarketplace.includes(m.mkp) &&
        // Only StubHub for international events, all for US events
        (m.mkp === Marketplace.StubHub || !isInternationalEvent)
    )
    .map((m: any) => m.mkp);

  // Show row if ANY selected marketplace is in the listed marketplaces
  return filterValue.some((selectedMkp) =>
    listedMarketplaces.includes(selectedMkp)
  );
};

/**
 * Money range filter - filters UiMoney objects by currency AND amount
 * Filters by both currency and amount range for accurate money filtering
 * Also filters by AutoPricingResultType for AllInPrice column
 */
export const moneyRangeFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: {
    currency?: string;
    min?: number;
    max?: number;
    autoPricingResultType?: AutoPricingResultType;
  }
): boolean => {
  const { currency, min, max, autoPricingResultType } = filterValue;

  // If no filters specified, show all
  if (min == null && max == null && !autoPricingResultType) {
    return true;
  }

  // Handle grouped rows
  if (row.getLeafRows().length > 0) {
    return row
      .getLeafRows()
      .some((leafRow) => moneyRangeFilter(leafRow, columnId, filterValue));
  }

  const value = row.getValue(columnId);
  if (value == null) {
    return false;
  }

  let amount: number;
  let valueCurrency: string | null = null;

  // Detect value type and extract amount + currency
  if (typeof value === 'object' && value !== null && 'amt' in value) {
    // UiMoney object: { amt, currency, disp, dec }
    const money = value as UiMoney;
    amount = money.amt;
    valueCurrency = money.currency;
  } else if (typeof value === 'number') {
    // Plain number - try to get currency from row.original
    amount = value;
    const listing = (row.original as any)?.listing;
    const sale = (row.original as any)?.sale;
    const purchase = row.original as any;
    valueCurrency =
      listing?.currency || sale?.currency || purchase?.currency || null;
  } else {
    return false;
  }

  // Filter by AutoPricingResultType if specified (for AllInPrice column)
  if (autoPricingResultType) {
    const listing = (row.original as any)?.listing;
    const isAutoPrc = listing?.isAutoPrc;
    const autoPrcResult = listing?.autoPrcResult;
    const autoPrcConstraints = listing?.autoPrcConstraintType;
    if (!isAutoPrc || (!autoPrcConstraints || autoPrcConstraints == AutoPricingConstraintType.None) || autoPrcResult !== autoPricingResultType) {
      return false;
    }
  }

  // If filter has currency specified, only show matching currency rows
  if (currency && valueCurrency !== currency) {
    return false;
  }

  // Filter by amount
  if (min != null && amount < min) return false;
  if (max != null && amount > max) return false;
  return true;
};
